import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { api, unwrap, type FindingDetail, type StatusOut } from "../../api/client";
import { StatusEditor } from "../../components/StatusEditor";
import { ErrorMessage, Loading, SeverityBadge } from "../../components/ui";
import { t } from "../../i18n/t";
import { confidenceLabel } from "../../lib/confidence";
import { formatMeters, formatNumber, formatSpeed } from "../../lib/format";
import { useQuery } from "../../lib/useQuery";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function nvdbRef(ref: Record<string, unknown>): string {
  const id = String(ref.veglenkesekvensid ?? "?");
  if (typeof ref.fra === "number" && typeof ref.til === "number") {
    return t("finding.nvdbSeq", { id, fra: ref.fra, til: ref.til });
  }
  return t("finding.nvdbPos", { id, pos: String(ref.posisjon ?? "?") });
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Details({ f }: { f: FindingDetail }) {
  const aadtYear = f.attrs.aadt_aar;
  const vegrefs = strings(f.attrs.vegreferanser);
  const objectTypes = Object.entries(f.nvdb_object_ids).filter(([, ids]) => ids.length > 0);
  return (
    <>
      <dl className="details">
        <Row label={t("finding.severity")}>
          <SeverityBadge severity={f.severity} />
        </Row>
        <Row label={t("finding.rank")}>{t("finding.rankValue", { rank: f.rank })}</Row>
        <Row label={t("finding.location")}>{f.location}</Row>
        <Row label={t("finding.schools")}>
          {f.school_names
            .map((name, i) => {
              const id = f.school_ids[i];
              const n = id ? f.school_exposure[id] : undefined;
              return n ? `${name} (${formatNumber(n)})` : name;
            })
            .join(", ")}
        </Row>
        <Row label={t("finding.exposure")}>
          {(f.exposure_low ?? f.exposure) !== (f.exposure_high ?? f.exposure)
            ? t("finding.exposureRangeValue", {
                low: formatNumber(f.exposure_low ?? f.exposure),
                high: formatNumber(f.exposure_high ?? f.exposure),
              })
            : t("finding.exposureValue", { count: formatNumber(f.exposure) })}
        </Row>
        {f.omgivelse && (
          <Row label={t("finding.surroundings")}>
            <strong>
              {f.omgivelse === "faa_boliger"
                ? t("finding.surroundingsFaaBoliger")
                : t("finding.surroundingsBoliger")}
            </strong>
            {f.adresser_langs != null && (
              <>
                <br />
                {f.omgivelse === "faa_boliger"
                  ? t(
                      f.kind === "strekning"
                        ? "finding.surroundingsFaaBoligerText"
                        : "finding.surroundingsFaaBoligerPointText",
                      {
                        count: formatNumber(f.adresser_langs),
                      },
                    )
                  : t("finding.surroundingsBoligerText", {
                      count: formatNumber(f.adresser_langs),
                    })}
              </>
            )}
          </Row>
        )}
        <Row label={t("finding.confidence")}>{confidenceLabel(f.confidence ?? "robust")}</Row>
        <Row label={t("finding.speed")}>{formatSpeed(f.speed)}</Row>
        <Row label={t("finding.aadt")}>
          {f.aadt === null
            ? t("common.unknown")
            : typeof aadtYear === "number"
              ? t("finding.aadtYear", { value: formatNumber(f.aadt), year: aadtYear })
              : t("finding.aadtValue", { value: formatNumber(f.aadt) })}
        </Row>
        {f.length_m !== null && <Row label={t("finding.length")}>{formatMeters(f.length_m)}</Row>}
        <Row label={t("finding.accidents")}>
          {t("finding.accidentsValue", {
            count: formatNumber(f.nearby_accidents),
            soft: formatNumber(f.nearby_soft_accidents),
          })}
        </Row>
        <Row label={t("finding.fau")}>
          {f.fau_confirmations > 0
            ? t("finding.fauValue", { count: formatNumber(f.fau_confirmations) })
            : t("common.none")}
        </Row>
      </dl>
      <h3>{t("finding.explanation")}</h3>
      <p>{f.explanation}</p>
      <h3>{t("finding.tiltak")}</h3>
      <p>{f.tiltak}</p>
      <details className="nvdb-details">
        <summary>{t("finding.nvdbRefs")}</summary>
        <dl className="details">
          <Row label={t("finding.criterion")}>
            <code>{f.criterion_id}</code>
          </Row>
          {vegrefs.length > 0 && (
            <Row label={t("finding.vegref")}>
              <ul className="plain-list">
                {vegrefs.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </Row>
          )}
          {f.nvdb_refs.length > 0 && (
            <Row label={t("finding.nvdbRefs")}>
              <ul className="plain-list">
                {f.nvdb_refs.map((r, i) => (
                  <li key={i}>{nvdbRef(r)}</li>
                ))}
              </ul>
            </Row>
          )}
          {objectTypes.length > 0 && (
            <Row label={t("finding.nvdbObjects")}>
              <ul className="plain-list">
                {objectTypes.map(([type, ids]) => (
                  <li key={type}>
                    {t("finding.objectType", { type: capitalize(type), ids: ids.join(", ") })}
                  </li>
                ))}
              </ul>
            </Row>
          )}
        </dl>
      </details>
    </>
  );
}

export function FindingPanel({
  orgId,
  nr,
  findingKey,
  onClose,
  onSaved,
}: {
  orgId: string;
  nr: string;
  findingKey: string;
  onClose: () => void;
  onSaved: (out: StatusOut) => void;
}) {
  const detail = useQuery(`finding:${orgId}:${nr}:${findingKey}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}/findings/{key}", {
        params: { path: { org_id: orgId, nummer: nr, key: findingKey } },
        signal,
      }),
    ),
  );
  const headingRef = useRef<HTMLHeadingElement>(null);
  const f = detail.data;

  // Move focus to the panel when a finding opens, so keyboard and screen reader users follow.
  useEffect(() => {
    if (f) headingRef.current?.focus();
  }, [f]);

  return (
    <section className="finding-panel" aria-label={t("finding.details")}>
      <div className="panel-header">
        <h2 ref={headingRef} tabIndex={-1}>
          {f?.title ?? t("finding.details")}
        </h2>
        <button
          type="button"
          className="btn btn-secondary btn-small"
          onClick={onClose}
          aria-label={t("finding.close")}
        >
          {t("common.close")}
        </button>
      </div>
      {detail.error && <ErrorMessage error={detail.error} onRetry={detail.reload} />}
      {!f && !detail.error && <Loading />}
      {f && (
        <>
          <Details f={f} />
          <h3>{t("finding.followUp")}</h3>
          <StatusEditor
            key={f.key}
            variant="panel"
            orgId={orgId}
            nr={nr}
            findingKey={f.key}
            title={f.title}
            status={f.status}
            comment={f.comment}
            updatedAt={f.status_updated_at}
            onSaved={onSaved}
          />
        </>
      )}
    </section>
  );
}
